/*
  Bundle selector and add-to-cart for the OraLux product section.

  No theme components are involved: the radios are named `id`, which is the field Shopify's
  product form already posts, so choosing a bundle is plain HTML and works with the script
  switched off. The script only adds what HTML cannot do on its own - repainting the headline
  price and adding to the cart without leaving the page.

  Deliberate points:

  - The prices come from a JSON block rendered by Liquid, not from parsing the markup. Money
    formatting varies by shop and locale, and reading it back out of the DOM would break the
    moment somebody switches currency.

  - The submit button is disabled while the request is in flight, so a double click cannot
    add the same bundle twice.

  - After a successful add the cart count is refreshed from /cart.js and a `cart:updated`
    event is dispatched. Themes listen for different things, so the section also falls back
    to sending the visitor to the cart when there is nothing listening.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-oralux-buy]';

  function money(cents, format) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return (format || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  function initOne(root) {
    if (root.hasAttribute('data-oralux-ready')) return;
    root.setAttribute('data-oralux-ready', '');

    var form = root.querySelector('form');
    var button = root.querySelector('[data-oralux-submit]');
    var errorEl = root.querySelector('[data-oralux-error]');

    var data = {};
    var payload = root.querySelector('[data-oralux-variants]');
    if (payload) {
      try {
        data = JSON.parse(payload.textContent);
      } catch (e) {
        data = {};
      }
    }

    /*
      A selector handed in from Liquid is not automatically a price element. Once it matched
      this very container - the attributes holding the selectors were named after the
      attributes they search for - and `textContent = ''` on the match erased the form, the
      variant radios and the Add to Cart button, with nothing in the console.

      The attribute names no longer collide, but the guard stays: anything that is the buy
      block or wraps it cannot be a price element, and writing text into it destroys the form.
      Cheaper to refuse than to debug a second time.
    */
    function priceTarget(selector) {
      if (!selector) return null;
      var el = document.querySelector(selector);
      if (!el || el === root || el.contains(root)) return null;
      return el;
    }

    function paint(variantId) {
      var variant = data.variants && data.variants[variantId];
      if (!variant) return;

      var now = priceTarget(root.dataset.oraluxSelPriceNow);
      var was = priceTarget(root.dataset.oraluxSelPriceWas);
      var save = priceTarget(root.dataset.oraluxSelPriceSave);

      if (now) now.textContent = money(variant.price, data.money_format);

      if (was) {
        was.textContent = variant.compare_at ? money(variant.compare_at, data.money_format) : '';
        was.hidden = !variant.compare_at;
      }

      if (save) {
        var diff = (variant.compare_at || 0) - variant.price;
        save.textContent = diff > 0 ? (data.save_label || 'Save {amount}').replace('{amount}', money(diff, data.money_format)) : '';
        save.hidden = diff <= 0;
      }
    }

    root.addEventListener('change', function (event) {
      var input = event.target.closest('input[name="id"]');
      if (!input) return;
      root.querySelectorAll('[data-oralux-option]').forEach(function (option) {
        option.toggleAttribute('data-current', option.contains(input));
      });
      paint(input.value);
    });

    if (!form || !button) return;

    /*
      When the form is a customized built-in the theme owns submission - Impact's
      `<form is="product-form">` adds to the cart and opens its drawer by itself. Binding our
      own handler on top would preventDefault and quietly replace that behaviour, so we only
      submit ourselves when nothing else is going to.
    */
    /*
      The theme owns submission: Impact's `<form is="product-form">` posts to the cart itself.
      Binding our own submit here would preventDefault and quietly replace that, so we stand
      aside and only keep the price repaint.
    */
    if (form.getAttribute('is')) return;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (button.hasAttribute('disabled')) return;

      var chosen = form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
      if (!chosen) return;

      button.setAttribute('disabled', '');
      button.setAttribute('aria-busy', 'true');
      if (errorEl) errorEl.hidden = true;

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(chosen.value), quantity: 1 }] }),
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) throw new Error(body.description || body.message || 'Add to cart failed');
            return body;
          });
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          if (root.dataset.oraluxAfterAdd === 'cart') {
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart';
          }
        })
        .catch(function (error) {
          if (errorEl) {
            errorEl.textContent = error.message;
            errorEl.hidden = false;
          }
        })
        .then(function () {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });
    });

    var initial = form.querySelector('input[name="id"]:checked');
    if (initial) paint(initial.value);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /*
    A fallback for upsell buttons that are not inside a product form. On Impact each row is a
    real `<form is="product-form">`, so the theme handles the add, the drawer markup and the
    busy state itself and this never runs. Delegated on the document because the rows sit
    outside the buy block.
  */
  document.addEventListener('click', function (event) {
    var add = event.target.closest('[data-oralux-upsell-add]');
    if (!add || add.hasAttribute('disabled')) return;
    event.preventDefault();

    var errorEl = document.querySelector('[data-oralux-error]');
    add.setAttribute('disabled', '');

    /* The same signal the theme's own form raises, so one rule styles both buttons. */
    add.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(add.dataset.oraluxUpsellAdd), quantity: 1 }],
      }),
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) throw new Error(body.description || 'Add to cart failed');
          return body;
        });
      })
      .then(function (added) {
        /*
          A drawer that opens on the theme's own add has to open on ours too. Impact's cart
          drawer listens for `variant:add` and wants the fresh cart alongside the added items,
          so the cart is fetched before announcing - announce first and the drawer opens
          showing what was in it a moment ago.
        */
        return fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var detail = { items: added.items || [added], cart: cart };
            add.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: detail }));
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } })
            );
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
            add.setAttribute('data-added', '');
          });
      })
      .catch(function (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
      })
      .then(function () {
        add.removeAttribute('disabled');
        add.removeAttribute('aria-busy');
      });
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
